import { useQuery } from "@tanstack/react-query";
import { useApiHeaders } from "@/hooks/use-api-headers";
import { useAdminOrgStore } from "@/stores/admin-org";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export type EntityType = "organisation" | "account" | "site" | "kitchen";

/** The four names `build_chain` gives the levels it walks. `source` is one of these or `platform`;
 * `invalid_at` is only ever one of these, since only a stored row can hold an unusable value. */
export type ConfigLevel = EntityType;

export interface ResolvedField {
  value: string | number | Record<string, unknown> | null;
  /** The level the usable value came from, `platform` for a `PLATFORM_DEFAULTS` entry, or `""` for
   * a field with neither. `resolve_fields` writes nothing else. */
  source: ConfigLevel | "platform" | "";
  source_id: string;
  source_name: string;
  /** The deepest level holding a value this field's declared shape rejects, when there is one —
   * `source` then names where the usable value came from instead, often `platform`. On every
   * response since POS2-1136 CP2 and unread by anything until POS2-1210. */
  invalid_at: ConfigLevel | null;
}

export interface ResolvedConfig {
  entity_type: string;
  entity_id: string;
  config: Record<string, ResolvedField>;
}

export function useResolvedConfig(entityType: EntityType, entityId: string | null) {
  const headers = useApiHeaders();
  // Scope the cache to the platform admin's active org (POS2-967): the same entityId can
  // resolve differently — or 404 — under a different X-Org-Id, so it must not be shared.
  const orgId = useAdminOrgStore((s) => s.orgId);

  return useQuery<ResolvedConfig>({
    queryKey: ["resolved-config", orgId, entityType, entityId],
    queryFn: async () => {
      const params = new URLSearchParams({
        entity_type: entityType,
        entity_id: entityId!,
      });
      const res = await fetch(`${API_URL}/api/v1/config/resolved?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to fetch resolved config");
      const json = await res.json();
      return json.data;
    },
    enabled: !!entityId && !!headers.Authorization.replace("Bearer ", ""),
    staleTime: 30_000,
  });
}
